/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../../../../context/AppContext";
import { Button } from "../../../../components/ui/button";
import { toast } from "../../../../components/ui/sonner";
import { Mail, Loader2, BookUser, Send } from "lucide-react";
import { T } from "../../../../lib/i18n";
import { SubmittalCard } from "./SubmittalCard";
import { SubmittalCompose } from "./SubmittalCompose";
import { SubmittalResultDialog } from "./SubmittalResultDialog";
import { AgencyContactsDialog } from "./AgencyContactsDialog";

export { STAGE_AGENCY } from "./constants";

/* 交图邮件闭环：撰写（预填机构邮箱 + 自动 CC）→ 线程时间线（送达 / 打开 / 回复）→ 记录审图结果 */
export const SubmittalPanel = ({ pid, stageKey, agencyTypes = [], meta, onChanged, compact, tid = "dv3-submittals" }) => {
  const { api } = useApp();
  const [sp] = useSearchParams();
  const focusSid = sp.get("sid");
  const [d, setD] = useState(null);
  const [compose, setCompose] = useState(false);
  const [result, setResult] = useState(null);
  const [contacts, setContacts] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const types = agencyTypes.join("|");
  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (stageKey) q.set("stageKey", stageKey);
    if (types) q.set("agencyTypes", types);
    return api.get(`/design/v3/projects/${pid}/submittals?${q.toString()}`).then(({ data }) => setD(data)).catch((e) => setD({ error: e?.detailText || e.message, items: [] }));
  }, [api, pid, stageKey, types]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged?.(); };
  const event = async (s, type) => {
    const note = window.prompt(type === "replied" ? T("机构回复摘要（可空）") : T("备注内容"));
    if (note === null || (type === "note" && !note.trim())) return;
    try { await api.post(`/design/v3/submittals/${s.id}/event`, { type, note }); toast.success(type === "replied" ? T("已标记为收到回复") : T("已添加备注")); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const items = d?.items || [];
  const shown = compact && !showAll ? items.filter((s, i) => i < 2 || s.id === focusSid) : items;
  const open = items.filter((s) => !s.result && !["failed", "bounced"].includes(s.status)).length;
  return (
    <div className={`rounded-lg border ${compact ? "border-sky-100 bg-sky-50/40" : "border-slate-200 bg-white"} p-2 space-y-1.5`} data-testid={`${tid}${stageKey ? `-${stageKey}` : ""}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-[10px] font-bold text-sky-700 uppercase tracking-wide flex items-center gap-1"><Mail className="h-3 w-3" />{T("交图邮件")} {items.length}{open ? <span className="text-amber-700 normal-case font-semibold">{T(" · {0} 封待结果", [open])}</span> : null}</p>
        {!d && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        {d?.compose && !d.compose.emailConfigured && <span className="text-[10px] text-red-600" data-testid={`${tid}-not-configured`}>{T("邮件服务未配置")}</span>}
        <span className="ml-auto inline-flex items-center gap-1">
          {meta.canEdit && <button type="button" data-testid={`${tid}-contacts-btn${stageKey ? `-${stageKey}` : ""}`} onClick={() => setContacts(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[10px] text-slate-500 hover:text-sky-700 inline-flex items-center gap-0.5"><BookUser className="h-3 w-3" />{T("机构联系人")}</button>}
          {meta.canEdit && <Button size="sm" className="h-6 text-[10px] bg-sky-600 hover:bg-sky-700" data-testid={`${tid}-compose-btn${stageKey ? `-${stageKey}` : ""}`} disabled={!d?.compose} onClick={() => setCompose(true)}><Send className="h-3 w-3 mr-0.5" />{T("撰写交图邮件")}</Button>}
        </span>
      </div>
      {d?.error && <p className="text-[11px] text-red-600">{d.error}</p>}
      {d && !items.length && <p className="text-[11px] text-slate-400" data-testid={`${tid}-empty`}>{T("尚未发送交图邮件 · 撰写时会预填机构邮箱（按城市记忆）并自动 CC 设计部经理 / 负责销售 / 发送人")}</p>}
      {shown.map((s) => <SubmittalCard key={s.id} s={s} meta={meta} compact={compact} focus={s.id === focusSid} onReplied={() => event(s, "replied")} onNote={() => event(s, "note")} onResult={() => setResult(s)} />)}
      {compact && items.length > 2 && <button type="button" data-testid={`${tid}-show-all`} onClick={() => setShowAll(!showAll)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[10px] text-sky-700 hover:underline">{showAll ? T("收起") : T("查看全部 {0} 封", [items.length])}</button>}
      {compose && d?.compose && <SubmittalCompose pid={pid} stageKey={stageKey} compose={d.compose} agencyTypes={agencyTypes} onClose={() => setCompose(false)} onSent={() => { setCompose(false); changed(); }} />}
      {result && <SubmittalResultDialog s={result} disciplines={d?.compose?.disciplines || []} onClose={() => setResult(null)} onDone={() => { setResult(null); changed(); }} />}
      {contacts && <AgencyContactsDialog city={d?.compose?.city || ""} agencyTypes={agencyTypes} knownTypes={(d?.compose?.contacts || []).map((c) => c.agencyType)} onClose={() => setContacts(false)} onChanged={load} />}
    </div>
  );
};
